import BodyHeader from "@app/shared/components/BodyHeader";
import { Icon } from "@design-system/Icon";
import { FlexCol } from "@design-system/Layout/Flex";
import { PageContainer } from "@design-system/Layout/PageContainer";
import {
    getPaginationRowModel,
    getSortedRowModel,
    Table,
} from "@design-system/Table";
import { Text } from "@design-system/Typography/Text";
import { useEffect, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { columns } from "./columns";
import { QuestionnairesDashboard } from "./components/QuestionnairesDashboard";
import { QuestionnaireTabHeader } from "./components/QuestionnaireTabHeader";
import { useQuestionnaires } from "./data";
import "./i18n";
import { keys, RESOURCE_NAME } from "./i18n";
import { getQuestionnaireStatus } from "./services";
import { type QuestionnaireStatus } from "./types";

export const ExternalQuestionnaires: FC = () => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { externalQuestionnaires, isPending } = useQuestionnaires();
    const [searchString, setSearchString] = useState<string | undefined>(
        undefined,
    );
    const [questionnaireStatus, setQuestionnaireStatus] = useState<
        QuestionnaireStatus[]
    >([]);

    const questionnairesFiltered = useMemo(
        () =>
            externalQuestionnaires.filter((questionnaire) => {
                if (
                    searchString &&
                    !questionnaire.name
                        .toLowerCase()
                        .includes(searchString.toLowerCase())
                ) {
                    return false;
                }

                if (
                    !!questionnaireStatus.length &&
                    !questionnaireStatus.includes(
                        getQuestionnaireStatus(questionnaire),
                    )
                ) {
                    return false;
                }
                return true;
            }),
        [externalQuestionnaires, searchString, questionnaireStatus],
    );

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    useEffect(() => {
        setPagination({ ...pagination, pageIndex: 0 });
    }, [setPagination, searchString, questionnaireStatus]);

    const tableOptions = {
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
        state: {
            pagination,
        },
        autoResetPageIndex: false,
    };

    return (
        <PageContainer>
            <BodyHeader
                title={t(keys.page_title)}
                subTitle={t(keys.page_subtitle)}
            />
            <FlexCol p="4" gap="6">
                <QuestionnairesDashboard />
                <FlexCol gap="4">
                    <QuestionnaireTabHeader
                        searchString={searchString}
                        setSearchString={setSearchString}
                        questionnaireStatus={questionnaireStatus}
                        setQuestionnaireStatus={setQuestionnaireStatus}
                    />
                    <Table
                        columns={columns}
                        data={questionnairesFiltered}
                        loading={isPending}
                        options={tableOptions}
                        EmptyBody={EmptyState}
                        variant="grid"
                    />
                </FlexCol>
            </FlexCol>
        </PageContainer>
    );
};

const EmptyState = () => {
    const { t } = useTranslation(RESOURCE_NAME);

    return (
        <FlexCol
            py="10"
            justifyContent="center"
            alignItems="center"
            w="full"
            className="bg-neutral-25"
        >
            <FlexCol gap="2" alignItems="center" className="my-8">
                <Icon name="search" />
                <FlexCol gap="1" alignItems="center">
                    <Text variant="body-sm-bold">
                        {t(keys.empty_state_title)}
                    </Text>
                    <Text variant="body-sm" className="font-light">
                        {t(keys.empty_state_subtitle)}
                    </Text>
                </FlexCol>
            </FlexCol>
        </FlexCol>
    );
};
